/**
 * Session Amazon tag assignment (`POST ecmtg.kueez.net/assign`).
 * Used when runtime `/assign/ad` is not active and the ad account has `estcb`.
 */

import { getSdkState } from '../../core/state';
import { buildCommonParams } from '../../core/analytics/commonParams';
import {
  mergeAttributionIdsInto,
} from '../../core/analytics/attributionIds';
import type { EventParams } from '../../core/analytics/track';
import { getCookie } from '../../util/cookies';
import type { SdkContext } from '../types';
import type { AdAccountConfig } from '../../core/config/types';
import { debug } from '../../util/log';

const ASSIGN_URL = 'https://ecmtg.kueez.net/assign';
const ASSIGN_TIMEOUT_MS = 8000;
const FBP_WAIT_MS = 2000;

function sessionTagStorageKey(bucketId: string): string {
  return `kz_ecmst_${bucketId}`;
}

export function getSessionTagForBucket(bucketId: string): string | null {
  try {
    return sessionStorage.getItem(sessionTagStorageKey(bucketId));
  } catch {
    return null;
  }
}

export function setSessionTagForBucket(bucketId: string, tag: string): void {
  try {
    sessionStorage.setItem(sessionTagStorageKey(bucketId), tag);
  } catch {
    /* ignore */
  }
}

function waitForCookie(name: string, timeoutMs: number): Promise<string | null> {
  const existing = getCookie(name);
  if (existing) return Promise.resolve(existing);
  if (typeof window === 'undefined') return Promise.resolve(null);

  return new Promise((resolve) => {
    const started = Date.now();
    const timer = setInterval(() => {
      const value = getCookie(name);
      if (value || Date.now() - started >= timeoutMs) {
        clearInterval(timer);
        resolve(value);
      }
    }, 50);
  });
}

function appendParams(search: URLSearchParams, params: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    search.set(key, String(value));
  }
}

type AssignResponse = {
  tag?: string;
  parent_tag?: string;
};

/**
 * Resolve a session attribution tag: cache → POST `/assign`.
 * Returns null when gated off or the request fails.
 */
export async function resolveSessionTag(
  ctx: SdkContext,
  adAccount: AdAccountConfig | null,
): Promise<string | null> {
  const bucketRaw = adAccount?.sessionTagChannelBucket;
  if (!bucketRaw) {
    debug('session assign skipped (no estcb)');
    return null;
  }
  const bucketId = String(bucketRaw);
  if (!bucketId || bucketId === '0') {
    debug('session assign skipped (estcb empty/zero)');
    return null;
  }

  const ecomAccountId = ctx.getConfig().ecom.ecomAccountId;
  if (!ecomAccountId) {
    debug('session assign skipped (no ecomAccountId)');
    return null;
  }

  const cached = getSessionTagForBucket(bucketId);
  if (cached) {
    debug('session assign cache hit', { bucketId, tag: cached });
    return cached;
  }

  if (adAccount.platform === 'facebook') {
    await waitForCookie('_fbp', FBP_WAIT_MS);
  }

  const state = getSdkState();
  const common = buildCommonParams(state);
  const params = new URLSearchParams();
  appendParams(params, common as Record<string, unknown>);
  params.set('ecmai', String(ecomAccountId));
  params.set('esabi', bucketId);

  if (typeof window !== 'undefined') {
    const ct = new URLSearchParams(window.location.search).get('ct');
    if (ct === 'p' || ct === 'c') params.set('ct', ct);
  }

  const assignFields: EventParams = {};
  mergeAttributionIdsInto(assignFields, {
    persistCookies: ctx.getConsent() === 'granted',
  });
  appendParams(params, assignFields as Record<string, unknown>);

  const assignUrl = `${ASSIGN_URL}?${params.toString()}`;
  debug('session assign request', { url: assignUrl });

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), ASSIGN_TIMEOUT_MS);

  try {
    const response = await fetch(assignUrl, {
      method: 'POST',
      signal: controller.signal,
    });
    if (!response.ok) {
      debug('session assign http error', { status: response.status });
      return null;
    }
    const json = (await response.json()) as AssignResponse;
    if (!json.tag) {
      debug('session assign invalid response', json);
      return null;
    }
    setSessionTagForBucket(bucketId, json.tag);
    debug('session assign ok', { tag: json.tag, parentTag: json.parent_tag });
    return json.tag;
  } catch (error) {
    debug('session assign failed', error);
    return null;
  } finally {
    clearTimeout(timeout);
  }
}
